import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconButton, Tooltip } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

export type MaximizeOverlayProps = {
  open: boolean;
};

function getAllDialogPapers(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>('.MuiDialog-paper'))
    // Nested detail dialogs (e.g. BankLoanDetail / VehicleLoanDetail) opt out
    .filter((el) => el.getAttribute('data-no-maximize') !== 'true');
}

function applyMaxStyles(paper: HTMLElement) {
  const container = (paper.closest('.MuiDialog-container') as HTMLElement | null) || (paper.parentElement as HTMLElement | null);
  const modalRoot = paper.closest('.MuiModal-root') as HTMLElement | null;
  const content = paper.querySelector<HTMLElement>('.MuiDialogContent-root');

  // Save originals
  paper.dataset._prevInlineStyle = paper.getAttribute('style') || '';
  if (container) {
    container.dataset._prevInlineStyle = container.getAttribute('style') || '';
  }
  if (modalRoot) {
    modalRoot.dataset._prevInlineStyle = modalRoot.getAttribute('style') || '';
  }
  if (content) {
    content.dataset._prevInlineStyle = content.getAttribute('style') || '';
  }

  // Ensure modal and container fill viewport
  if (modalRoot) {
    modalRoot.style.setProperty('position', 'fixed', 'important');
    modalRoot.style.setProperty('inset', '0', 'important');
    modalRoot.style.setProperty('width', '100dvw', 'important');
    modalRoot.style.setProperty('height', '100dvh', 'important');
    modalRoot.style.setProperty('display', 'flex', 'important');
  }
  if (container) {
    container.style.setProperty('align-items', 'stretch', 'important');
    container.style.setProperty('justify-content', 'center', 'important');
    container.style.setProperty('width', '100dvw', 'important');
    container.style.setProperty('height', '100dvh', 'important');
    container.style.setProperty('padding', '0', 'important');
  }

  // Make paper truly fullscreen
  paper.style.setProperty('box-sizing', 'border-box', 'important');
  paper.style.setProperty('display', 'flex', 'important');
  paper.style.setProperty('flex-direction', 'column', 'important');
  paper.style.setProperty('align-items', 'stretch', 'important');
  paper.style.setProperty('max-width', 'none', 'important');
  paper.style.setProperty('width', '100dvw', 'important');
  paper.style.setProperty('height', '100dvh', 'important');
  paper.style.setProperty('max-height', '100dvh', 'important');
  paper.style.setProperty('margin', '0', 'important');
  paper.style.setProperty('border-radius', '0', 'important');

  // Make content fill remaining space and scroll
  if (content) {
    content.style.setProperty('flex', '1 1 auto', 'important');
    content.style.setProperty('min-height', '0', 'important');
    content.style.setProperty('overflow', 'auto', 'important');
  }
}

function restoreStyles(paper: HTMLElement) {
  const container = paper.parentElement as HTMLElement | null;
  const modalRoot = paper.closest('.MuiModal-root') as HTMLElement | null;
  const content = paper.querySelector<HTMLElement>('.MuiDialogContent-root');
  const prevPaper = paper.dataset._prevInlineStyle ?? '';
  paper.setAttribute('style', prevPaper);
  delete paper.dataset._prevInlineStyle;
  if (container) {
    const prevContainer = container.dataset._prevInlineStyle ?? '';
    container.setAttribute('style', prevContainer);
    delete container.dataset._prevInlineStyle;
  }
  if (modalRoot) {
    const prevModal = modalRoot.dataset._prevInlineStyle ?? '';
    modalRoot.setAttribute('style', prevModal);
    delete modalRoot.dataset._prevInlineStyle;
  }
  if (content) {
    const prevContent = content.dataset._prevInlineStyle ?? '';
    content.setAttribute('style', prevContent);
    delete content.dataset._prevInlineStyle;
  }
}

export default function MaximizeOverlay({ open }: MaximizeOverlayProps) {
  const [papers, setPapers] = useState<HTMLElement[]>([]);
  const maximizedSetRef = useRef<Set<HTMLElement>>(new Set());
  const observerRef = useRef<MutationObserver | null>(null);

  // Locate the active dialog paper whenever dialog open state changes
  useEffect(() => {
    if (!open) return;
    setPapers(getAllDialogPapers());
  }, [open]);

  // Observe DOM changes to re-bind if paper changes (e.g., lazy mount)
  useEffect(() => {
    if (!open) return;
    const obs = new MutationObserver(() => {
      setPapers(getAllDialogPapers());
    });
    obs.observe(document.body, { childList: true, subtree: true });
    observerRef.current = obs;
    return () => {
      obs.disconnect();
      observerRef.current = null;
    };
  }, [open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      maximizedSetRef.current.forEach((p) => restoreStyles(p));
      maximizedSetRef.current.clear();
    };
  }, []);

  const toggleFor = (paperEl: HTMLElement) => {
    const isMax = maximizedSetRef.current.has(paperEl);
    if (!isMax) {
      applyMaxStyles(paperEl);
      maximizedSetRef.current.add(paperEl);
    } else {
      restoreStyles(paperEl);
      maximizedSetRef.current.delete(paperEl);
    }
    // trigger re-render of buttons icon state
    setPapers(getAllDialogPapers());
  };

  const renderButtonFor = (paperEl: HTMLElement) => {
    const maximized = maximizedSetRef.current.has(paperEl);
    const button = (
      <div
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 2000,
          pointerEvents: 'auto',
        }}
      >
        <Tooltip title={maximized ? 'Restore' : 'Maximize'} placement="bottom">
          <IconButton size="small" onClick={() => toggleFor(paperEl)} sx={{ bgcolor: 'rgba(0,0,0,0.04)' }}>
            {maximized ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </div>
    );
    return createPortal(button, paperEl);
  };

  if (!open || papers.length === 0) return null;
  return <>{papers.map((p) => renderButtonFor(p))}</>;
}
